/**
 * Reports Page
 *
 * Route: /reports | Nav: Reports > Reports
 *
 * Browse available report types and generate downloadable reports.
 */
import React, { useState } from 'react';
import {
  Body1,
  Body2,
  Card,
  CardHeader,
  Badge,
  tokens,
  Spinner,
  Button,
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogContent,
  DialogActions,
  Field,
  Input,
  Dropdown,
  Option,
} from '@fluentui/react-components';
import {
  DocumentRegular,
  ArrowDownloadRegular,
} from '@fluentui/react-icons';
import { useQuery, useMutation } from '@tanstack/react-query';
import { reportsApi, complianceApi } from '../services/api';
import { usePageStyles } from '../styles/page-styles';
import { PageHeader } from '../components/common/PageHeader';
import type { ReportType, GenerateReportRequest, ComplianceFramework } from '../types';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const getCategoryColor = (
  category: ReportType['category']
): 'informative' | 'success' | 'important' | 'warning' => {
  switch (category) {
    case 'Diagnostic':
      return 'informative';
    case 'Compliance':
      return 'success';
    case 'Evidence':
      return 'important';
    case 'ROI':
      return 'warning';
    default:
      return 'informative';
  }
};

/**
 * Default report types — always available even before API seeds tenant data.
 */
const DEFAULT_REPORT_TYPES: ReportType[] = [
  {
    id: 'executive-daily-brief',
    name: 'Executive Daily Brief',
    description:
      'Summary of governance health, enforcement activity, and top risks. Designed for executive stakeholders who need a quick daily posture check.',
    category: 'Diagnostic',
    supportsFrameworkFilter: false,
    supportsDateRange: true,
  },
  {
    id: 'weekly-assurance-summary',
    name: 'Weekly Assurance Summary',
    description:
      'Framework coverage changes, evidence continuity metrics, and remediation SLA adherence over the past 7 days.',
    category: 'Compliance',
    supportsFrameworkFilter: true,
    supportsDateRange: true,
  },
  {
    id: 'audit-evidence-pack',
    name: 'Audit Evidence Pack',
    description:
      'Per-framework evidence export for auditors. Includes evidence records, integrity hashes, control mappings, and chain-of-custody documentation.',
    category: 'Evidence',
    supportsFrameworkFilter: true,
    supportsDateRange: true,
  },
  {
    id: 'governance-execution',
    name: 'Governance Execution Report',
    description:
      'Comprehensive policy execution summary including policies enforced, controls verified, evidence generated, and enforcement outcomes over the reporting period.',
    category: 'Compliance',
    supportsFrameworkFilter: true,
    supportsDateRange: true,
  },
];

// ---------------------------------------------------------------------------
// GenerateReportDialog
// ---------------------------------------------------------------------------

const GenerateReportDialog: React.FC<{
  reportType: ReportType | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}> = ({ reportType, open, onOpenChange }) => {
  const [frameworkCode, setFrameworkCode] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // Fetch frameworks if the report supports framework filter
  const { data: frameworks } = useQuery({
    queryKey: ['complianceFrameworks'],
    queryFn: () => complianceApi.getFrameworks(),
    enabled: open && !!reportType?.supportsFrameworkFilter,
  });

  // Generate mutation
  const generateMutation = useMutation({
    mutationFn: (request: GenerateReportRequest) => reportsApi.generate(request),
    onSuccess: (blob) => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${reportType?.name || 'report'}-${new Date().toISOString().slice(0, 10)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      onOpenChange(false);
    },
  });

  const handleGenerate = () => {
    if (!reportType) return;
    const request: GenerateReportRequest = {
      reportTypeId: reportType.id,
    };
    if (reportType.supportsFrameworkFilter && frameworkCode) {
      request.frameworkCode = frameworkCode;
    }
    if (reportType.supportsDateRange) {
      if (dateFrom) request.from = new Date(dateFrom).toISOString();
      if (dateTo) request.to = new Date(dateTo).toISOString();
    }
    generateMutation.mutate(request);
  };

  if (!reportType) return null;

  return (
    <Dialog open={open} onOpenChange={(_, data) => onOpenChange(data.open)}>
      <DialogSurface style={{ maxWidth: '480px' }}>
        <DialogTitle>Generate Report: {reportType.name}</DialogTitle>
        <DialogBody>
          <DialogContent>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: tokens.spacingVerticalM,
              }}
            >
              <Body1>{reportType.description}</Body1>

              {reportType.supportsFrameworkFilter && (
                <Field label="Framework" hint="Optional: filter by compliance framework">
                  <Dropdown
                    placeholder="All Frameworks"
                    value={frameworkCode}
                    onOptionSelect={(_, data) =>
                      setFrameworkCode((data.optionValue as string) || '')
                    }
                    style={{ minWidth: '240px' }}
                  >
                    <Option value="">All Frameworks</Option>
                    {(frameworks || []).map((fw: ComplianceFramework) => (
                      <Option key={fw.code} value={fw.code}>
                        {fw.name}
                      </Option>
                    ))}
                  </Dropdown>
                </Field>
              )}

              {reportType.supportsDateRange && (
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: tokens.spacingHorizontalM,
                  }}
                >
                  <Field label="From">
                    <Input
                      type="date"
                      value={dateFrom}
                      onChange={(_, data) => setDateFrom(data.value)}
                    />
                  </Field>
                  <Field label="To">
                    <Input
                      type="date"
                      value={dateTo}
                      onChange={(_, data) => setDateTo(data.value)}
                    />
                  </Field>
                </div>
              )}

              {generateMutation.isError && (
                <Body2 style={{ color: tokens.colorPaletteRedForeground1 }}>
                  Failed to generate report. Please try again.
                </Body2>
              )}
            </div>
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              appearance="primary"
              icon={<ArrowDownloadRegular />}
              onClick={handleGenerate}
              disabled={generateMutation.isPending}
            >
              {generateMutation.isPending ? 'Generating...' : 'Generate'}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

// ---------------------------------------------------------------------------
// ReportsPage
// ---------------------------------------------------------------------------

export const ReportsPage: React.FC = () => {
  const ps = usePageStyles();
  const [selectedReport, setSelectedReport] = useState<ReportType | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  // Fetch report types — merge with defaults so page is never empty
  const { data: apiReportTypes, isLoading } = useQuery({
    queryKey: ['reportTypes'],
    queryFn: () => reportsApi.getTypes(),
  });

  // Use API types if available, otherwise fall back to built-in defaults
  const reportTypes =
    apiReportTypes && apiReportTypes.length > 0
      ? apiReportTypes
      : DEFAULT_REPORT_TYPES;

  const handleGenerateClick = (reportType: ReportType) => {
    setSelectedReport(reportType);
    setDialogOpen(true);
  };

  // Loading state
  if (isLoading) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          padding: tokens.spacingVerticalXXXL,
        }}
      >
        <Spinner size="large" label="Loading report types..." />
      </div>
    );
  }

  return (
    <div className={ps.page}>
      <PageHeader title="Reports" subtitle="Generated compliance reports and audit-ready documentation" />

      <div className={ps.metricGrid}>
        {reportTypes.map((rt) => (
          <Card key={rt.id}>
            <CardHeader
              image={
                <DocumentRegular
                  style={{
                    fontSize: '1.5rem',
                    color: tokens.colorBrandForeground1,
                  }}
                />
              }
              header={<Body1 style={{ fontWeight: 600 }}>{rt.name}</Body1>}
              description={
                <Badge appearance="filled" color={getCategoryColor(rt.category)}>
                  {rt.category}
                </Badge>
              }
            />
            <div className={ps.cardBody}>
              <Body2 style={{ color: tokens.colorNeutralForeground3 }}>
                {rt.description}
              </Body2>
              <Button
                appearance="primary"
                icon={<ArrowDownloadRegular />}
                onClick={() => handleGenerateClick(rt)}
                style={{ alignSelf: 'flex-start' }}
              >
                Generate
              </Button>
            </div>
          </Card>
        ))}
      </div>

      {/* Generate Dialog */}
      <GenerateReportDialog
        reportType={selectedReport}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
      />
    </div>
  );
};
